import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import axios from 'axios'
import moment from 'moment-timezone'
import PaymentForm from '../components/PaymentForm'
import { getSignupType, requiresDetail, formatSlotLabel, signupCtaLabel } from '../utils/signupSheets'

const ET = 'America/New_York'

const linkifyText = (text) => {
  if (!text) return text
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const parts = text.split(urlRegex)
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-800 underline">{part}</a>
    ) : part
  )
}

const EventDetail = () => {
  const { id } = useParams()
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lightboxImage, setLightboxImage] = useState(null)
  const [showRsvpModal, setShowRsvpModal] = useState(false)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [rsvpComplete, setRsvpComplete] = useState(false)
  const [confirmationNumber, setConfirmationNumber] = useState(null)
  const [numAdults, setNumAdults] = useState(1)
  const [numChildren, setNumChildren] = useState(0)
  const [attendeeName, setAttendeeName] = useState('')
  const [attendeeEmail, setAttendeeEmail] = useState('')
  const [showSignupModal, setShowSignupModal] = useState(false)
  const [signupSlots, setSignupSlots] = useState(null)
  const [selectedRole, setSelectedRole] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [signupName, setSignupName] = useState('')
  const [signupEmail, setSignupEmail] = useState('')
  const [signupDetail, setSignupDetail] = useState('')
  const [signupComplete, setSignupComplete] = useState(false)
  const [signupSubmitting, setSignupSubmitting] = useState(false)

  const categories = [
    { value: 'all', label: 'All Events', color: 'gray' },
    { value: 'social', label: 'Social Events', color: 'blue' },
    { value: 'sports', label: 'Sports & Recreation', color: 'green' },
    { value: 'meeting', label: 'Meetings', color: 'purple' },
    { value: 'maintenance', label: 'Maintenance', color: 'orange' },
    { value: 'special', label: 'Special Events', color: 'pink' },
    { value: 'holiday', label: 'Holiday Events', color: 'red' }
  ]

  const getDriveImageUrl = (evt) => {
    if (evt.flyerDriveFileId) {
      return `https://lh3.googleusercontent.com/d/${evt.flyerDriveFileId}`
    }
    if (evt.flyerImageUrl) {
      const match = evt.flyerImageUrl.match(/[?&]id=([^&]+)/)
      if (match) {
        return `https://lh3.googleusercontent.com/d/${match[1]}`
      }
    }
    return evt.flyerImageUrl
  }

  const getEventStatus = (startDate) => {
    const today = moment().startOf('day')
    const eventDate = moment.tz(startDate, ET).startOf('day')
    if (eventDate.isBefore(today)) {
      return { status: 'past', label: 'Past Event', color: 'gray' }
    } else if (eventDate.isSame(today)) {
      return { status: 'today', label: 'Today', color: 'green' }
    } else if (eventDate.diff(today, 'days') <= 7) {
      return { status: 'soon', label: 'This Week', color: 'blue' }
    } else {
      return { status: 'upcoming', label: 'Upcoming', color: 'purple' }
    }
  }

  const getCategoryColor = (category) => {
    const cat = categories.find(c => c.value === category)
    return cat ? cat.color : 'gray'
  }

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`/api/events/${id}`)
        setEvent(response.data)
      } catch (err) {
        console.error('Error fetching event:', err)
        if (err.response?.status === 404) {
          setError('Event not found.')
        } else {
          setError('Failed to load event. Please try again later.')
        }
      } finally {
        setLoading(false)
      }
    }
    fetchEvent()
  }, [id])

  const calculateTotal = () => {
    if (!event || !event.collectPayment) return 0
    const adultTotal = (event.pricePerAdult || 0) * numAdults
    const childTotal = (event.pricePerChild || 0) * numChildren
    return adultTotal + childTotal
  }

  const handleRsvpClick = () => {
    setNumAdults(1)
    setNumChildren(0)
    setAttendeeName('')
    setAttendeeEmail('')
    setShowRsvpModal(true)
  }

  const handleRsvpSubmit = async () => {
    try {
      const response = await axios.post(`/api/events/${event._id}/rsvp`, {
        attendeeName,
        attendeeEmail,
        numAdults,
        numChildren
      })
      setConfirmationNumber(response.data.confirmationNumber)
      setShowRsvpModal(false)
      setRsvpComplete(true)
    } catch (err) {
      console.error('Error submitting RSVP:', err)
      alert('Failed to submit RSVP. Please try again.')
    }
  }

  const handleContinueToPayment = () => {
    setShowRsvpModal(false)
    setShowPaymentForm(true)
  }

  const handlePaymentSuccess = async (payment) => {
    try {
      const confirmationNum = `RSVP-${Date.now()}`
      await axios.post('/api/events/rsvp-confirmation', {
        eventId: event._id,
        eventTitle: event.title,
        eventDate: event.eventDate,
        eventLocation: event.location,
        attendeeName,
        attendeeEmail,
        numAdults,
        numChildren,
        totalAmount: payment.amounts.total,
        paymentId: payment.id,
        confirmationNumber: confirmationNum
      })
      setConfirmationNumber(confirmationNum)
      setShowPaymentForm(false)
      setRsvpComplete(true)
    } catch (err) {
      console.error('Error processing RSVP:', err)
      alert('Payment successful! However, there was an error sending the confirmation email. Please contact us at admin@lockridgeforestswimtennis.com')
    }
  }

  const handlePaymentCancel = () => {
    setShowPaymentForm(false)
    setShowRsvpModal(true)
  }

  const resetRsvpState = () => {
    setShowRsvpModal(false)
    setShowPaymentForm(false)
    setRsvpComplete(false)
    setNumAdults(1)
    setNumChildren(0)
    setConfirmationNumber(null)
    setAttendeeName('')
    setAttendeeEmail('')
  }

  const handleSignupClick = async () => {
    try {
      const response = await axios.get(`/api/events/${event._id}/signup-slots`)
      setSignupSlots(response.data.roles)
      setSelectedRole(null)
      setSelectedSlot(null)
      setSignupName('')
      setSignupEmail('')
      setSignupDetail('')
      setSignupComplete(false)
      setShowSignupModal(true)
    } catch (err) {
      console.error('Error fetching signup slots:', err)
      alert('Failed to load signup sheet. Please try again.')
    }
  }

  const handleSignupSubmit = async () => {
    if (!canSubmitSignup) return
    try {
      setSignupSubmitting(true)
      await axios.post(`/api/events/${event._id}/signup`, {
        roleId: selectedRole,
        slotId: selectedSlot,
        attendeeName: signupName,
        attendeeEmail: signupEmail,
        detail: signupDetail,
      })
      setSignupComplete(true)
    } catch (err) {
      console.error('Error submitting signup:', err)
      alert(err.response?.data?.message || 'Failed to sign up. Please try again.')
    } finally {
      setSignupSubmitting(false)
    }
  }

  const handleSignupAnother = async () => {
    try {
      const response = await axios.get(`/api/events/${event._id}/signup-slots`)
      setSignupSlots(response.data.roles)
      setSelectedRole(null)
      setSelectedSlot(null)
      setSignupDetail('')
      setSignupComplete(false)
    } catch (err) {
      console.error('Error refreshing signup slots:', err)
    }
  }

  const formatSlotTime = (timeStr) => {
    if (!timeStr) return ''
    return moment(timeStr, 'HH:mm').format('h:mm A')
  }

  const selectedRoleObj = signupSlots?.find(r => r.roleId === selectedRole)
  const canSubmitSignup =
    !!selectedRole &&
    !!selectedSlot &&
    !!signupName.trim() &&
    !!signupEmail.trim() &&
    (!requiresDetail(selectedRoleObj) || !!signupDetail.trim())

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container-custom py-20 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{error || 'Event not found'}</h2>
          <p className="text-gray-600 mb-6">The event you're looking for may have been removed or doesn't exist.</p>
          <Link to="/events" className="btn btn-primary">
            Back to All Events
          </Link>
        </div>
      </div>
    )
  }

  const eventStatus = getEventStatus(event.eventDate)
  const categoryColor = getCategoryColor(event.category)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Banner */}
      <div className={`bg-${categoryColor}-600 text-white`}>
        <div className="container-custom py-10">
          <Link to="/events" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors text-sm">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to All Events
          </Link>
          <div className="flex flex-wrap gap-2 mb-3">
            {event.category && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                {categories.find(c => c.value === event.category)?.label || event.category}
              </span>
            )}
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
              {eventStatus.label}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{event.title}</h1>
          <p className="text-white/90 text-lg">
            {moment.tz(event.eventDate, ET).format('dddd, MMMM DD, YYYY')}
            {event.startTime && ` at ${moment(event.startTime, 'HH:mm').format('h:mm A')}`}
            {event.endTime && ` - ${moment(event.endTime, 'HH:mm').format('h:mm A')}`}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom py-10">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-6 md:p-8 space-y-6">
              {/* Flyer Image */}
              {event.flyerImageUrl && (
                <div>
                  <img
                    src={getDriveImageUrl(event)}
                    alt={`${event.title} flyer`}
                    className="w-full rounded-lg shadow-md cursor-pointer hover:opacity-95 transition-opacity"
                    onClick={() => setLightboxImage({ url: getDriveImageUrl(event), title: event.title })}
                  />
                  <p className="text-xs text-gray-500 mt-1 text-center">Click image to enlarge</p>
                </div>
              )}

              {/* Description */}
              {event.description && (
                <div>
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Description</h2>
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">{linkifyText(event.description)}</p>
                </div>
              )}

              {/* Event Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {event.location && (
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">Location</p>
                      <p className="text-gray-900">{event.location}</p>
                    </div>
                  </div>
                )}
                {event.organizer && (
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">Organizer</p>
                      <p className="text-gray-900">{event.organizer}</p>
                    </div>
                  </div>
                )}
                {event.endDate && moment.tz(event.endDate, ET).isAfter(moment.tz(event.eventDate), 'day') && (
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">Ends</p>
                      <p className="text-gray-900">{moment.tz(event.endDate, ET).format('MMMM DD, YYYY')}</p>
                    </div>
                  </div>
                )}
                {event.contactEmail && (
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase">Contact</p>
                      <a href={`mailto:${event.contactEmail}`} className="text-primary-600 hover:text-primary-800">{event.contactEmail}</a>
                    </div>
                  </div>
                )}
              </div>

              {/* Pricing info */}
              {event.collectPayment && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-primary-900 mb-2">Pricing</h3>
                  <div className="text-sm text-primary-800 space-y-1">
                    {event.pricePerAdult > 0 && <p>${event.pricePerAdult.toFixed(2)} per adult</p>}
                    {event.pricePerChild > 0 && <p>${event.pricePerChild.toFixed(2)} per child</p>}
                  </div>
                </div>
              )}

              {/* RSVP Button */}
              {eventStatus.status !== 'past' && (event.requireRsvp || event.collectPayment) && (
                <button
                  onClick={handleRsvpClick}
                  className="btn btn-primary w-full text-center"
                >
                  {event.collectPayment ? 'RSVP & Pay' : 'RSVP for Event'}
                </button>
              )}

              {/* Sign Up for Shifts Button */}
              {eventStatus.status !== 'past' && event.hasSignupSheet && (
                <button
                  onClick={handleSignupClick}
                  className="btn btn-primary w-full text-center bg-orange-600 hover:bg-orange-700"
                >
                  {signupCtaLabel(event.signupRoles)}
                </button>
              )}
            </div>
          </div>

          {/* Back link at bottom */}
          <div className="mt-8 text-center">
            <Link to="/events" className="text-primary-600 hover:text-primary-800 font-medium inline-flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              View All Events
            </Link>
          </div>
        </div>
      </div>

      {/* Payment Form Modal */}
      {showPaymentForm && event && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-hidden">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8 max-h-[90vh] flex flex-col overflow-hidden">
            <div className="bg-white border-b px-6 py-4 flex justify-between items-center flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-900">
                Complete Payment for {event.title}
              </h3>
              <button
                onClick={handlePaymentCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-6 overflow-y-auto flex-1">
              <PaymentForm
                amount={calculateTotal()}
                description={`${event.title} - ${numAdults} adult(s), ${numChildren} child(ren)`}
                customerEmail={attendeeEmail}
                customerName={attendeeName}
                referenceId={event._id}
                metadata={{
                  eventId: event._id,
                  eventTitle: event.title,
                  numAdults,
                  numChildren,
                  eventDate: event.eventDate,
                  attendeeName,
                  attendeeEmail
                }}
                onSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
              />
            </div>
          </div>
        </div>
      )}

      {/* RSVP Confirmation Modal */}
      {rsvpComplete && event && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-secondary-100 mb-4">
                <svg className="h-10 w-10 text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">RSVP Confirmed!</h3>
                <p className="text-gray-600 mb-4">
                  {event.collectPayment
                    ? 'Your payment has been processed successfully.'
                    : 'Your registration has been submitted successfully.'}
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Confirmation Number:</span>
                      <span className="font-semibold text-gray-900">{confirmationNumber}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Event:</span>
                      <span className="font-semibold text-gray-900">{event.title}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-semibold text-gray-900">
                        {moment.tz(event.eventDate, ET).format('MMMM DD, YYYY')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Attendees:</span>
                      <span className="font-semibold text-gray-900">
                        {numAdults} adult(s), {numChildren} child(ren)
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  A confirmation email has been sent to your email address.
                </p>
                <button onClick={resetRsvpState} className="btn btn-primary w-full">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RSVP Modal */}
      {showRsvpModal && event && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center rounded-t-lg">
              <h3 className="text-xl font-bold text-gray-900">RSVP for Event</h3>
              <button
                onClick={() => setShowRsvpModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">{event.title}</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {moment.tz(event.eventDate, ET).format('MMMM DD, YYYY')}
                  </div>
                  {event.startTime && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {moment(event.startTime, 'HH:mm').format('h:mm A')}
                    </div>
                  )}
                  {event.location && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {event.location}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={attendeeName}
                    onChange={(e) => setAttendeeName(e.target.value)}
                    placeholder="Enter your full name"
                    className="input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={attendeeEmail}
                    onChange={(e) => setAttendeeEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="input w-full"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Confirmation will be sent to this email address
                  </p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Adults</label>
                  <input
                    type="number"
                    min="0"
                    value={numAdults}
                    onChange={(e) => setNumAdults(parseInt(e.target.value) || 0)}
                    className="input w-full"
                  />
                  {event.collectPayment && event.pricePerAdult > 0 && (
                    <p className="text-sm text-gray-600 mt-1">${event.pricePerAdult.toFixed(2)} per adult</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Children</label>
                  <input
                    type="number"
                    min="0"
                    value={numChildren}
                    onChange={(e) => setNumChildren(parseInt(e.target.value) || 0)}
                    className="input w-full"
                  />
                  {event.collectPayment && event.pricePerChild > 0 && (
                    <p className="text-sm text-gray-600 mt-1">${event.pricePerChild.toFixed(2)} per child</p>
                  )}
                </div>
              </div>

              {event.collectPayment && (
                <div className="mb-6 p-4 bg-primary-50 rounded-lg border border-primary-200">
                  <div className="space-y-2 text-sm">
                    {numAdults > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-700">Adults ({numAdults} x ${event.pricePerAdult.toFixed(2)})</span>
                        <span className="font-semibold text-gray-900">${(numAdults * event.pricePerAdult).toFixed(2)}</span>
                      </div>
                    )}
                    {numChildren > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-700">Children ({numChildren} x ${event.pricePerChild.toFixed(2)})</span>
                        <span className="font-semibold text-gray-900">${(numChildren * event.pricePerChild).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-primary-300">
                      <span className="font-bold text-gray-900">Total</span>
                      <span className="font-bold text-primary-600 text-lg">${calculateTotal().toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button onClick={() => setShowRsvpModal(false)} className="btn btn-secondary flex-1">Cancel</button>
                {event.collectPayment ? (
                  <button
                    onClick={handleContinueToPayment}
                    disabled={(numAdults === 0 && numChildren === 0) || !attendeeName.trim() || !attendeeEmail.trim()}
                    className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue to Payment
                  </button>
                ) : (
                  <button
                    onClick={handleRsvpSubmit}
                    disabled={(numAdults === 0 && numChildren === 0) || !attendeeName.trim() || !attendeeEmail.trim()}
                    className="btn btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Submit RSVP
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Signup Modal */}
      {showSignupModal && event && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center rounded-t-lg">
              <h3 className="text-xl font-bold text-gray-900">
                {signupComplete ? 'Signed Up!' : signupCtaLabel(signupSlots)}
              </h3>
              <button
                onClick={() => setShowSignupModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {signupComplete ? (
                <div className="text-center py-6">
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                    <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">You're signed up!</h4>
                  <p className="text-gray-600 mb-6">Thank you! We have you down.</p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleSignupAnother}
                      className="btn btn-secondary flex-1"
                    >
                      Sign Up for Something Else
                    </button>
                    <button
                      onClick={() => setShowSignupModal(false)}
                      className="btn btn-primary flex-1"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : signupSlots ? (
                <>
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">{event.title}</h4>
                    <p className="text-sm text-gray-600">
                      {moment.tz(event.eventDate, ET).format('MMMM DD, YYYY')}
                    </p>
                  </div>

                  <div className="space-y-4 mb-6">
                    {signupSlots.map(role => (
                      <div key={role.roleId}>
                        <h5 className="text-sm font-semibold text-gray-800">{role.roleName}</h5>
                        <p className="text-xs text-gray-500 mb-2">
                          {role.description || getSignupType(role).selectPrompt}
                        </p>
                        <div className="space-y-2 ml-2">
                          {role.slots.map(slot => {
                            const isSelected = selectedRole === role.roleId && selectedSlot === slot.slotId
                            return (
                              <button
                                key={slot.slotId}
                                type="button"
                                disabled={slot.isFull}
                                onClick={() => {
                                  setSelectedRole(role.roleId)
                                  setSelectedSlot(slot.slotId)
                                  setSignupDetail('')
                                }}
                                className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                                  slot.isFull
                                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                    : isSelected
                                    ? 'border-orange-500 bg-orange-50 text-orange-800'
                                    : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                                }`}
                              >
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-sm">
                                    {formatSlotLabel(role, slot, formatSlotTime)}
                                  </span>
                                  <span className={`text-xs font-medium ${
                                    slot.isFull ? 'text-red-500' : 'text-green-600'
                                  }`}>
                                    {slot.isFull ? 'Full' : `${slot.capacity - slot.filledCount} spot${slot.capacity - slot.filledCount !== 1 ? 's' : ''} left`}
                                  </span>
                                </div>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-4 mb-6">
                    {requiresDetail(selectedRoleObj) && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          {getSignupType(selectedRoleObj).detailLabel} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={signupDetail}
                          onChange={(e) => setSignupDetail(e.target.value)}
                          placeholder={getSignupType(selectedRoleObj).detailPlaceholder}
                          className="input w-full"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Your Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={signupName}
                        onChange={(e) => setSignupName(e.target.value)}
                        placeholder="Enter your full name"
                        className="input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Your Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={signupEmail}
                        onChange={(e) => setSignupEmail(e.target.value)}
                        placeholder="Enter your email address"
                        className="input w-full"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowSignupModal(false)}
                      className="btn btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSignupSubmit}
                      disabled={!canSubmitSignup || signupSubmitting}
                      className="btn btn-primary flex-1 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {signupSubmitting ? 'Signing up...' : 'Sign Up'}
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Flyer Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <div className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center">
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={lightboxImage.url}
              alt={`${lightboxImage.title} flyer`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default EventDetail
